<!--
Timetable screen
@author bmk11u
-->
<template>
  <div
    id="contain"
    class="container"
  >
    <Nav name="Timetable" :step="true" />
    <div class="viewSelector">
      <div class="viewSelectorColumn" :class="currentView == 'Day' ? 'selectedView' : null"><button @click="prevView = currentView; currentView='Day'; handleTimeframeChange('init')" :aria-pressed="currentView == 'Day' ? 'true' : 'false'">Day</button></div>
      <div class="viewSelectorColumn" :class="currentView == 'Week' ? 'selectedView' : null"><button @click="prevView = currentView; currentView='Week'; handleTimeframeChange('init')" :aria-pressed="currentView == 'Week' ? 'true' : 'false'">Week</button></div>
      <div class="viewSelectorColumn" :class="currentView == 'Month' ? 'selectedView' : null"><button @click="prevView = currentView; currentView='Month'; handleTimeframeChange('init')" :aria-pressed="currentView == 'Month' ? 'true' : 'false'">Month</button></div>
    </div>
    <Spinner ref="spinner_ref" />

    <Announcement category="43" :canHide="true"/>

    <div v-if="currentView == 'Week' || currentView == 'Day'" :class="['timeframeControlsContainer fc-header-toolbar', currentView]">
      <div v-if="currentView == 'Week'" id="weekSelected">
        <p id="weekLabel">{{ weekSelectionDisplay }}</p>
      </div>
      <div id="buttonWrapper">
        <button @click="todayTimeFrame" type="button" title="Today's Date" aria-pressed="false" class="fc-button-primary fc-customToday-button">Today</button>
        <button @click="handleTimeframeChange('previous')" aria-label="Previous" aria-pressed="false" class="fc-button-primary fc-customPrev-button"></button>
        <button @click="handleTimeframeChange('next')" aria-label="Next" aria-pressed="false" class="fc-button-primary fc-customNext-button"></button>
      </div>
    </div>

    <!-- Month View -->
    <main v-show="currentView == 'Month'" class="px-2">
      <FullCalendar id="calendar" ref='fc_ref' :options="calendarOptions" />
    </main>
    <NewModalPopup 
        v-if="showMonthOptions"
        :showX="true"
        title="Select a month"
        id="monthModal"
        @close="toggleMonthModal()"
    >
      <ul
        role="listbox"
      >
        <li 
            v-for="(monthText, dateStr) in monthOptions"
            :key="dateStr"
            :class="[
                'month-option', monthText === monthPickerText ? 'selected' : null
            ]"
            :date="dateStr"
            aria-selected="true"
            @click="monthSelected($event, dateStr)"
            @keyup.enter="monthSelected($event, dateStr)"
            role="option"
            tabindex="0"
        >
            {{ monthText }}
        </li>
      </ul>
    </NewModalPopup>
    <DailyAgenda v-if="currentView == 'Month'" :eventsList="eventsList" :viewContext="'Month'" :date="new Date(currentDateSelected)" :moveUp="moveAgendaUp()"/>

    <!-- Week View -->
    <div v-if="currentView == 'Week'" class="weekViewContainer" aria-labelledby="weekLabel">
        <div v-bind:key="index" v-for="(day, index) in weekdaysToDisplay" class="dayAgendaContainer">
          <DailyAgenda :eventsList="eventsList" :viewContext="'Week'" :date="new Date(day)" @weekExists="weekHandler"/>
        </div>
        <div v-if="weekAgendaBlank==true" class="nothing-scheduled"><p>Nothing scheduled</p></div>
    </div>


    <!-- Day View -->
    <div v-if="currentView == 'Day'" class="weekViewContainer">
        <DailyAgenda :eventsList="eventsList" :viewContext="'Day'" :date="new Date(timeframe)"/>
    </div>

    <div id="pusher" />
    <Footer active="timetable" />
  </div>
</template>

<script setup>
//Utilities
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import axios from 'axios'

//Full Calendar Plugin
import FullCalendar from '@fullcalendar/vue3'
import momentPlugin from '@fullcalendar/moment'
import interactionPlugin from '@fullcalendar/interaction'
import multiMonthPlugin from '@fullcalendar/multimonth'

//Components
import Nav from '@/components/Navbar.vue'
import Spinner from '@/components/Spinner.vue'
import DailyAgenda from '@/components/timetable/DailyAgenda.vue'
import NewModalPopup from '@/components/NewModalPopup.vue'
import Footer from '@/components/FooterMenu.vue'
import Announcement from '@/components/Announcement.vue'

//Composables/Helper Functions
import * as lst from '@/components/localStorage.js'
import { useCalendarMethods } from '@/composables/CalendarMethods.js'
const { fc, dateToUnixTs, moment } = useCalendarMethods()

//Component refs
const spinner_ref = ref(null)
const fc_ref = ref(null)


const noTimetable = ref(lst.getValue('noTimetable') === 'true')

//DailyAgenda component details 
const eventsList = ref([])
const prevView = ref(getDefaultView())
const currentView = ref(getDefaultView())
const currentDateSelected = ref(null)
const weekdaysToDisplay = ref([])
const weekSelectionDisplay = ref('')
const timeframe = ref(new Date(Date.now())) // this holds the current view timeframe
const weekAgendaBlank = ref(true)
const fromWeek = ref(false)

//Monthview
const monthFirstRender = ref(false)
const monthVisible = ref('')
const tallMonths = ref(false)
const updateScrollOffscreen = ref('')

//Month Picker
const monthRange = ref(getMonthRange())
const monthOptions = ref({})
const showMonthOptions = ref(false)
const monthPickerText = ref('')

//Full Calendar options object
const calendarOptions = ref({
  plugins: [ momentPlugin, interactionPlugin, multiMonthPlugin ],
  weekNumberCalculation: 'ISO',
  contentHeight: 'auto',
  initialView: 'customMultiMonth',
  visibleRange: {
    start: monthRange.value.startMonth,
    end: monthRange.value.endMonth
  },
  multiMonthMaxColumns: 1,
  eventClick: onEventClick,
  dateClick: onDateClick,
  eventDisplay: 'list-item',
  views: {
    customMultiMonth: {
        type: 'multiMonth',
        dayHeaderClassNames: headerClasses,
        dayHeaderContent: headerInitials,
        showNonCurrentDates: false,
        fixedWeekCount: false,
        viewDidMount: addMonthPicker,
    }
  },
  customButtons: {
    customNext: {
      text: '',
      click: onCustomNext,
      hint: 'Next Month'
    },
    customPrev: {
      text: '',
      click: onCustomPrev,
      hint: 'Previous Month'
    },
    customToday: {
      text: 'Today',
      click: onCustomToday,
      hint: 'Today\'s Date'
    }
  },
  headerToolbar: {
    left: '',
    center: '',
    right: 'customToday,customPrev,customNext'
  },
  buttonText: { month: 'MONTH' },
  eventContent: renderEvent,
})


onMounted(() => {
  getWeekdaysToDisplay(timeframe.value)
  handleTimeframeChange('init') // initialise

  if (noTimetable.value) {
    // this.alert('You don\'t have any timetabled classes or room bookings.')
  } else {
    // Timetable now cached on server so tell fc to fetch.
    fc(fc_ref).addEventSource(getEvents)
  }
  //Get difference between time now and time in storage
  var diff = timeframe.value - lst.getValue('timetableTime')
  var refreshRate = 900000 //15 minutes in miliseconds

  //If the time between now and the last timetable update is greater than 15 minutes get latest timetable
  if (diff > refreshRate) {
    spinner_ref.value.spin()
    axios.get('/timetable/timetable')
      .then(response => {
        fc(fc_ref).refetchEvents()
        lst.setValue('timetableTime', timeframe.value)
        lst.setValue('noTimetable', response.data.noTimetable)
      })
      .finally(() => {
        spinner_ref.value.stop()
      })
  }

  //Set current date for month view agenda
  currentDateSelected.value = new Date()

  //Populate object of months for month picker
  createMonthOptions()

  //Event listener for day grid month picker
  //Global click listner because month select added on the fly
  document.addEventListener('click', monthSelectHandlerModal)
  document.addEventListener('keyup', keyUpHandler)

  //Update aria label to be month name in month picker button
  document.querySelector('.fc-view-harness').setAttribute('aria-labelledby','monthPick')
})

onUnmounted(() => {
  //Remove the event listener for the day grid month select so doesn't trigger multiple times when re-visited
  document.removeEventListener('click', monthSelectHandlerModal)
  document.addEventListener('keyup', keyUpHandler)
})

//Watch for month picker modal. When shown, center the current date on next tick once the list is populated
watch(showMonthOptions, (newValue) => {
    if (newValue) {
      nextTick(() => {
        centerMonthModal()
        document.querySelector('#monthModal .closeBtn').focus()
      });
    }
});

//Watch for the month view to be shown. Call updateSize function when it does
//There is a weird bug where it doesn't render our styles until it is resized :\
//Mark that this is the first time the month view has been shown on screen and horixontal scroll to the current month
watch(currentView, (newValue) => {
    if (newValue == 'Month') {
      nextTick(() => {
        fc(fc_ref).updateSize()
        if(!monthFirstRender.value) {
            let scrollCont = document.querySelector('.fc-customMultiMonth-view')
            scrollCont.addEventListener("scroll", hideAgendaScroll);
            identifyTallMonths()
            scrollToToday()
            monthFirstRender.value = true
        }
      });
    }

    if (newValue == 'Week' || newValue == 'Day') {
      nextTick(() => {
        checkNavbuttons()
      })
    }
    
});

function getDefaultView() {
  switch(lst.getValue('timetableDefaultView')) {
    case 'D':
      return 'Day'
    case 'W':
      return 'Week'
    case 'M':
      return 'Month'
    default:
      return 'Week'
  }

}

/* Fetches events from server. Used over FullCalendar internal method so we can
    pass back login credentials. */
function getEvents (info, successCallback) { /* commented out 'failureCallback' */
  spinner_ref.value.spin()
  axios.get('/timetable/timetable/events', {
    params: {
      start: dateToUnixTs(info.start),
      end: dateToUnixTs(info.end),
      ts: Date.now()
    }
  })
    .then(response => {
      successCallback(response.data)
    })
    .finally(() => {
      spinner_ref.value.stop()
    })
}

/*********************************
  Daily Agenda functions
*********************************/

// Handles the change in timeframes for the DailyAgenda components
// Updates appropriate variables representing what date(s) should be highlighted/displayed. 
function handleTimeframeChange(direction) {
  let info = {start: null, end: null}
  let endDate = new Date(timeframe.value)
  //Reset the week agenda blank variable
  weekAgendaBlank.value = true
  if (direction == 'next') { // forward arrow clicked, user wants to see next timeframe
    switch(currentView.value) {
      // Are we in the day view?
      case 'Day':
        // Update the timeframe to the start of the next day 
        timeframe.value = new Date(new Date(timeframe.value.setDate(timeframe.value.getDate() + 1)))
         // ... and set the end date accordingly
        endDate = new Date(timeframe.value)
        //Set current date indicator for UI update later
        currentDateSelected.value = timeframe.value

        // Set the hours on both the start and end of the timeframe. This ensures we're getting all the possible events that can occur in a day
        timeframe.value.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        info = {start: timeframe.value, end: endDate}
        break
        // Are we in the week view?
      case 'Week':
        // Calculate days until the start of next week in order to update appropriately
        let daysUntilNextMonday = 8 - timeframe.value.getDay()
        if (daysUntilNextMonday == 8) { daysUntilNextMonday = 1 }

        // Update the timeframe to the start of next week
        timeframe.value = new Date(new Date(timeframe.value.setDate(timeframe.value.getDate() + daysUntilNextMonday)))
        // ... and set the end date accordingly
        //Setting end date to equal timeframe first before adding days because it was producing an error when overlapping months. It would set end date to prevous month not sure.  Not sure why but this fixes it. 
        endDate = new Date(timeframe.value)
        endDate.setDate(endDate.getDate() + 7)

        //Set current date indicator for UI update later
        currentDateSelected.value = timeframe.value

        // Set the hours on both the start and end of the timeframe. This ensures we're getting all the possible events that can occur in a day
        timeframe.value.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        info = {start: timeframe.value, end: endDate}
        break
    }
  } else if (direction == 'previous') { // back arrow clicked, user wants to see the previous timeframe
    switch(currentView.value) {
      // Are we in the day view?
      case 'Day':
        // Update the timeframe to the start of the previous day
        timeframe.value = new Date(new Date(timeframe.value.setDate(timeframe.value.getDate() - 1)))
        // ...and set the end date accordingly
        endDate = new Date(timeframe.value)
        //Set current date indicator for UI update later
        currentDateSelected.value = timeframe.value

        // Set the hours on both the start and end of the timeframe. This ensures we're getting all the possible events that can occur in a day
        timeframe.value.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        info = {start: timeframe.value, end: endDate}
        break
        // Are we in the week view?
      case 'Week':

        // Calculate days until the start of this week in order to update appropriately
        let daysUntilPrevMonday =  timeframe.value.getDay() - 1 
        if (daysUntilPrevMonday == 0) { daysUntilPrevMonday = 7 } 

        // Update the timeframe to the start of next week
        timeframe.value = new Date(new Date(timeframe.value.setDate(timeframe.value.getDate() - daysUntilPrevMonday)))
        endDate = new Date(endDate.setDate(timeframe.value.getDate() + 7))

        //Set current date indicator for UI update later
        currentDateSelected.value = timeframe.value

        // Set the hours on both the start and end of the timeframe. This ensures we're getting all the possible events that can occur in a day
        timeframe.value.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        info = {start: timeframe.value, end: endDate}
        break
    }
  } else if (direction == 'init') { // Used for when a view is selected or when the component initially mounts.
    let daysUntilPrevMonday = timeframe.value.getDay() - 1
    if (daysUntilPrevMonday == -1) {
      daysUntilPrevMonday = 6
    }
    switch(currentView.value) {
      // Are we switching to the day view?
      case 'Day':
        
        //If had been previously viewing week view
        //Check if todays date is in that week
        //Show today's date if so 
        //If not, show the Monday of that week
        
        if(fromWeek.value) {
            if(checkTodayInWeek()) {
              timeframe.value = new Date()
            } else {
              //Set it to the Monday at the start of that week
              timeframe.value = new Date(timeframe.value.setDate(timeframe.value.getDate() - daysUntilPrevMonday))
            }
        }
        endDate = new Date(timeframe.value)
        currentDateSelected.value = timeframe.value

        //Only want to caculate a date based on week view once while still in this view so set to false
        fromWeek.value = false
        
        break
        // Are we switching to the week view?
      case 'Week':

        //Set this flag to true so that the next view can adapt dates if necessary
        fromWeek.value = true

        timeframe.value = new Date(timeframe.value.setDate(timeframe.value.getDate() - daysUntilPrevMonday))

        // Update the endDate to reflect a week's timespan
        endDate = new Date(endDate.setDate(timeframe.value.getDate() + 7))
        currentDateSelected.value = timeframe.value
        break
        // Are we switching to the month view?
      case 'Month':
    
        if(fromWeek.value) {
          if(checkTodayInWeek()) {
            timeframe.value = new Date()
          } else {
            //Set it to the Monday at the start of that week
            timeframe.value = new Date(timeframe.value.setDate(timeframe.value.getDate() - daysUntilPrevMonday))
          }
        }
        currentDateSelected.value = timeframe.value

        //Set timeframe start to first day of month
        timeframe.value = new Date(timeframe.value.getFullYear(), timeframe.value.getMonth(), 1)
        // Set timeframe to last day of month
        endDate = new Date(timeframe.value.getFullYear(), timeframe.value.getMonth() + 1, 0)

        //Only want to caculate a date based week view once per action while still in this view so set to false
        fromWeek.value = false

        break
    }

    // Set the hours on both the start and end of the timeframe. This ensures we're getting all the possible events that can occur in a day
    timeframe.value.setHours(0, 0, 0, 0)
    endDate.setHours(23, 59, 59, 999)
    info = {start: timeframe.value, end: endDate}
  }

  //currentDateSelected.value = timeframe.value
  updateAfterNav(currentDateSelected.value, 'offscreen')
  // Re-fetch events based on this newly selected view.
  getEvents(info, (response) => {eventsList.value = response})
  getWeekdaysToDisplay()
}

function checkTodayInWeek() {
    //When switching from week view to another view.
    //Check if today's date falls within the week that was being viewed
    //Set timeframe date to today's date

    let today = new Date()

    //If previous view was week and timeframe is not already today
    if(prevView.value == 'Week') { 
      //Convert todays date same format at weekdaystoDisplay array
      today = today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
      //If today is in the weekdays currently shown
      if(weekdaysToDisplay.value.includes(today)) {
        return true
      } else {
        return false
      }
    }
}

// Function for 'today' button on Day/Week view
// Resets the timeframe to todays date and calls handle function
function todayTimeFrame(){
  timeframe.value = new Date()
  handleTimeframeChange('init')
}

// Calcuate the weekdays to display for the DailyAgenda week view
// Updates weekdaysTodisplay, which is an array of days for which events may or may not take place. This is passed as a prop to DailyAgenda.
function getWeekdaysToDisplay() {
  const currentDate = new Date(timeframe.value)
  const currentDay = currentDate.getDay() // 0 for Sunday, 1 for Monday, ..., 6 for Saturday
  const days = []

  // Find the start of the current week.
  const monday = new Date(currentDate)
  monday.setDate((currentDate.getDate() - currentDay) + (currentDay === 0 ? -6 : 1))

  // Iterate though all the days in the week and add them to the days array
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday)
    date.setDate(monday.getDate() + i)
    days.push(date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }))
  }

  weekdaysToDisplay.value = days
  formatWeekSelection()
}

function formatWeekSelection() {
  let startDate = new Date(weekdaysToDisplay.value[0])
  let startDay = startDate.getDate()
  let startMonth = startDate.toLocaleString('default', { month: 'short' });
  let startYear = startDate.getFullYear()

  let endDate = new Date(weekdaysToDisplay.value[6])
  let endDay = endDate.getDate()
  let endMonth = endDate.toLocaleString('default', { month: 'short' });
  let endYear = endDate.getFullYear()


  let weekStr = `${startMonth} ${startDay}`
  //If years aren't the same, include start year
  weekStr += (startYear == endYear) ? ' - ' : `, ${startYear} - `
  //If months aren't the same, include end month
  weekStr += (startMonth == endMonth) ? '' : `${endMonth} `
  weekStr += `${endDay}, ${endYear}`

  weekSelectionDisplay.value = weekStr

}

/*********************************
  Fullcalendar callback functions
*********************************/ 
//Callback for rendering an event on the month view
function renderEvent (arg) {
  //Create return element
  const ret = document.createElement('div')

  if(arg.view.type == 'customMultiMonth') {
    //Add classes to return element to show dot
    let classes = 'event-monthview fc-daygrid-event-dot'
    ret.setAttribute('class', classes)
    const txt = document.createTextNode(' ')
    ret.appendChild(txt)
  }

  return {
    domNodes: [ret]
  }
}

//Update month day indicator and set date of event clicked
function onEventClick (info) {
    currentDateSelected.value = info.event.start
    //Update daily agenda
    timeframe.value = info.event.start
    updateDayIndicator(info.el)
}

//Update the month view to show date selected and set current date selected varaible
function onDateClick (info) {
  currentDateSelected.value = info.date
  //Update daily agenda
  timeframe.value = info.date
  updateDayIndicator(info.dayEl)
}

//Give week days different classes to weekend days
function headerClasses(arg) {
  let className = ''
  if (arg.text == 'Sat' || arg.text == 'Sun') {
    className = 'weekend-day'
  } else {
    className = 'week-day'
  }

  return className
}

//Show the inital of the day of the week in table header
function headerInitials(arg) {
  //Just the initial
  return arg.text.slice(0,1)
}


/*********************************
  Month view - month picker functions
*********************************/ 
//Create list of months to choose from
function createMonthOptions() {
  let dateNow = new Date()
  let month = dateNow.getMonth()
  let year = dateNow.getFullYear()

  //Create array of dates for month select
  for(let i = monthRange.value.goBack; i < monthRange.value.goForward+1; i++){
    let date = new Date(year, month + i, 1)
    monthOptions.value[formatSelectDate(date)] = date.toLocaleString("default", { month: "long", year: "numeric"})
  }
}

/* Format dates for month picker */
function formatSelectDate(date) {
  let year = date.getFullYear()
  //Plus 1 because javascript months start at 0 = January
  let month = (date.getMonth()+1).toString().padStart(2,0)
  return year+'-'+month
}

//Add the month picker element to the calendar month view
function addMonthPicker(arg) {
  if(arg.view.type == 'customMultiMonth') {

    //Set up observer and default properties
    let observer = new IntersectionObserver(onIntersection, {
      root: document.querySelector('.fc-view-harness'),
      threshold: 0.5 //do callback when 50% of element is in view
    })
    //Apply the observer to all the month container elements
    const elements = document.querySelectorAll('.fc-multimonth-month')
    elements.forEach(el => observer.observe(el))

    //Check if monthpicker already exists, add if not
    if(!document.getElementById('monthPick')) {

      let dateValue = new Date()
      //Format date into 'Month Year' text e.g May 2024
      let monthYear = dateValue.toLocaleString("default", { month: "long", year: "numeric"})
      monthPickerText.value = monthYear

      //Create the days of the week calendar table header and the monthpicker elements
      //I tried to do this seperately and add them both individually but it wouldn't display them in the correct location so created wrapper div container and hardcoded the html inside not ideal :/
      let wrapperDiv = document.createElement('div')
      wrapperDiv.innerHTML ='<table id="daysHeader" aria-hidden="true" class="fc-multimonth-header-table"></table><div id="uogMonthPick"><div id="selectWrap"><button id="monthPick">'+monthPickerText.value+'</button></div></div>'

      //Check if the calendar container has rendered 
      let calendarBox = document.querySelector('#calendar')
      if (calendarBox) {
        //If the days of the week header has not been added yet then add the new one and the month picker
        //This means these elemnts will only be added once and not multiple times if switining to week or day new and back to month
        if (!document.getElementById('daysHeader')) {
          //Put the new days of the week header and month picker elements after the navigation buttons
          calendarBox.insertBefore(wrapperDiv, calendarBox.children[1])
          //Get the contents of the existing days of the week header and copy that into the new days of the week header
          let tableHeader = document.querySelector('.fc-multimonth-header .fc-multimonth-header-table')
          daysHeader.innerHTML = tableHeader.innerHTML
        }
      }
    }
  }
}

//Show modal if month picker element is target of click
function monthSelectHandlerModal(e) {
  if(e.target.id === "monthPick") {
    toggleMonthModal()
  }
}

//Month selected from options, navigate to it, update UI, close modal and return focus to button
function monthSelected(e) {

  //Get date and scroll to new positon
  let targetDate = e.target.getAttribute("date")

  updateAfterNav(targetDate)
  handleTimeframeChange('init')

  //Close modal and return focus to month select button
  toggleMonthModal()
  document.getElementById('monthPick').focus()
}

function toggleMonthModal() {
  showMonthOptions.value = !showMonthOptions.value
}

//Month modal center the currently selected month
function centerMonthModal() {
  let modal = document.querySelector('#monthModal .modal-body');
  let selectedItem = modal.querySelector('.selected');

  if (selectedItem) {
      //Distance from selected item to top of modal
      let selectedItemOffset = selectedItem.offsetTop;
      //Height of selected element
      let selectedItemHeight = selectedItem.offsetHeight;
      //Height of modal
      let modalHeight = modal.clientHeight;

      //Scroll position to center the modal
      //Adjust the modal scrollTop to the top of the selected item minus half the height of the modal plus half the height of the selected item, so the selected item is centered.
      let scrollPosition = selectedItemOffset - (modalHeight / 2) + (selectedItemHeight / 2);
      modal.scrollTop = scrollPosition;
  }
}

/*********************************
  Month view functions
*********************************/ 
function getMonthRange() {

  //How many months into the past and future
  let goBack = -6
  let goForward = 6

  let today = new Date()
  let month = today.getMonth()
  let year = today.getFullYear()


  let startDate = new Date(year, month + goBack, 1)
  //Need to take timezone into consideration otherwise ISO string can give us 30th June instead of 1st July for example.
  //time zone offset is in minnutes so need to multiply by 60000 to get into miliseconds which is the format getTime returns
  //https://stackoverflow.com/a/29774197
  let start = new Date(startDate.getTime() - (startDate.getTimezoneOffset() * 60000)).toISOString().split('T')[0]

  //Plus 1 the month because javascript starts 0 = January not 1
  let endDate = new Date(year, month + (goForward+1), 1)
  let end = new Date(endDate.getTime() - (endDate.getTimezoneOffset() * 60000)).toISOString().split('T')[0]


  return {
    goBack: goBack,
    goForward: goForward,
    startMonth: start,
    endMonth: end
  }
}

//Update the month view to put the indicator circle around the currently selected day
function updateDayIndicator(element) {

  //Get currently selected day. If exists remove the indicator class from it
  let todayIndicator = document.querySelector('.fc-day.fc-day-today')
  if( todayIndicator ) {
    todayIndicator.classList.remove('fc-day-today')
  }
  //Get the closest element .fc-day elements to the element that was clicked and add the indicator class to it
  element.closest('.fc-day').classList.add('fc-day-today')

  //Check if the date getting set matches the date in day/week timeframe
  let timeframeFormatted = moment(timeframe.value, fc_ref).format("YYYY-MM-DD")
  if(element.dataset.date) {
    //If they don't match update the timaframe value
    if(timeframeFormatted != element.dataset.date) {
      
      timeframe.value = new Date(element.dataset.date)
    }
  }

}

//Check if currently viewing first or last month
//Disable prev or next button accordingly
function checkNavbuttons() {

  if(currentView.value === 'Month') {

    const prevButton = document.querySelector('#calendar .fc-header-toolbar .fc-button-primary.fc-customPrev-button')
    const nextButton = document.querySelector('#calendar .fc-header-toolbar .fc-button-primary.fc-customNext-button')

    //Get the date range from the monthOptions object keys
    let dateRange = Object.keys(monthOptions.value)

    if(monthVisible.value === dateRange[0]) {
      prevButton.classList.add('disable-btn')
      prevButton.disabled = true
    } else {
      prevButton.classList.remove('disable-btn')
      prevButton.disabled = false
    }

    if(monthVisible.value === dateRange[dateRange.length-1]) {
      nextButton.classList.add('disable-btn')
      nextButton.disabled = true
    } else {
      nextButton.classList.remove('disable-btn')
      nextButton.disabled = false
    }
  }

  if(currentView.value === 'Week') {

    const prevButton = document.querySelector('.timeframeControlsContainer .fc-button-primary.fc-customPrev-button')
    const nextButton = document.querySelector('.timeframeControlsContainer .fc-button-primary.fc-customNext-button')

    //Check the buttons exist (may not have rendered yet)
    if(prevButton && nextButton) {
      //If the first date in the week range is less than or equal to the start date of our range then blank it out
      let weekRangeStart = new Date(timeframe.value).getTime()
      let weekRangeEnd = new Date(timeframe.value)
      //Set week range end to 7 days after weekRangeStart
      weekRangeEnd.setDate(weekRangeEnd.getDate()+7)
      weekRangeEnd = weekRangeEnd.getTime()

      let rangeStart = new Date(monthRange.value.startMonth).getTime()
      let rangeEnd = new Date(monthRange.value.endMonth)
      //Set end range to 1 day before end of month range
      rangeEnd.setDate(rangeEnd.getDate()-1)
      rangeEnd = rangeEnd.getTime()

      if(weekRangeStart <= rangeStart) {
        prevButton.classList.add('disable-btn')
        prevButton.disabled = true
      } else {
        prevButton.classList.remove('disable-btn')
        prevButton.disabled = false
      }

      if(weekRangeEnd >= rangeEnd) {
        nextButton.classList.add('disable-btn')
        nextButton.disabled = true
      } else {
        nextButton.classList.remove('disable-btn')
        nextButton.disabled = false
      }
    }
  }

if(currentView.value === 'Day') {

  const prevButton = document.querySelector('.timeframeControlsContainer .fc-button-primary.fc-customPrev-button')
  const nextButton = document.querySelector('.timeframeControlsContainer .fc-button-primary.fc-customNext-button')

  //Check the buttons exist (may not have rendered yet)
  if(prevButton && nextButton) {

    //If the first date in the week range is less than or equal to the start date of our range then blank it out
    let dayDisplayed = new Date(timeframe.value).getTime()

    let rangeStart = new Date(monthRange.value.startMonth).getTime()
    let rangeEnd = new Date(monthRange.value.endMonth)
    //Set end range to 1 day before end of month range
    rangeEnd.setDate(rangeEnd.getDate()-1)
    rangeEnd = rangeEnd.getTime()

    
    if(dayDisplayed <= rangeStart) {
      prevButton.classList.add('disable-btn')
      prevButton.disabled = true
    } else {
      prevButton.classList.remove('disable-btn')
      prevButton.disabled = false
    }

    if(dayDisplayed >= rangeEnd) {
      nextButton.classList.add('disable-btn')
      nextButton.disabled = true
    } else {
      nextButton.classList.remove('disable-btn')
      nextButton.disabled = false
    }
  }
}

  
}

//After navigating to a month or clicking a day in a month
//Update the UI to navigate to that month/day
//Update data variables with the new dates and correct formatting
function updateAfterNav(targetDate, direction = '') {

  if(direction === 'next' || direction === 'prev') {
    targetDate = new Date(targetDate)

    if (direction === 'next') {
      targetDate.setMonth(targetDate.getMonth() + 1);
    }

    if (direction === 'prev') {
      targetDate.setMonth(targetDate.getMonth() - 1);
    }

    targetDate = moment(targetDate, fc_ref).format("YYYY-MM")

  }

  //If direction offscreen it means we have passed a full date not a yyyy-mm formatted one
  //yyyy-mm suggests it's a swipe between months
  let fullDate = ''
  if(direction == 'offscreen'){
    fullDate = targetDate
    updateScrollOffscreen.value = fullDate
    targetDate = moment(targetDate, fc_ref).format("YYYY-MM")
  }

  if(direction !== 'swipe'){
    //Get the month element to scroll to
    let monthElement = document.querySelector(`[data-date='${targetDate}']`)


    if(monthElement) {

      //Set the scrollbox position to the month elements scroll position
      document.querySelector('.fc-customMultiMonth-view').scrollLeft = monthElement.offsetLeft

    }
  }

  
  //Highlight todays date if navigating to a month via swipe or month picker and month is current month
  if(direction != 'offscreen') {
    //Get the current date in yyyy-mm
    let currentMonth = moment(new Date(), fc_ref).format("YYYY-MM")
    //If it's the current month
    if(targetDate === currentMonth) {
      //Set to today's date
      targetDate = moment(new Date(), fc_ref).format("YYYY-MM-DD")
    }
  }

  //TODO: DM figure out why this is here and what it does when more time
  //Something to do with avoiding the 'swipe' functionality from defaulting the selection to first day of the month
  //Using the full date if we have it otherwise using the month date and therefore default to 1st of month
  //Sorry for whoever reads this
  let sameMonth = moment(document.querySelector('.fc-day-today').dataset.date, fc_ref).format("YYYY-MM") == targetDate

  let date = ''
  if(direction === 'offscreen' || sameMonth) {
    if(!fullDate) {
      fullDate = document.querySelector('.fc-day-today').dataset.date
    }
    date = new Date(fullDate)
  } else {
    date = new Date(targetDate)
  }
  //Format date as 'Month Year' e.g 'May 2024'
  let monthYear = date.toLocaleString("default", { month: "long", year: "numeric"})
  //Format date as YYYY-MM-DD
  let formatDate = moment(date, fc_ref).format("YYYY-MM-DD")

  //Get the first day of month and update UI to show that day selected
  let firstDateCell = document.querySelector(`td[data-date="${formatDate}"]`)
  updateDayIndicator(firstDateCell)

  //Get the month picker element and update it's text and value of currently selected
  let monthPickElement = document.getElementById('monthPick')
  //Button text
  monthPickElement.innerText = monthYear
  //Selected month data
  monthPickerText.value = monthYear

  //Update date for Daily agenda
  currentDateSelected.value = date

  checkNavbuttons()
  hidePreviousDates()
}
      
//Custom functions for the navigation buttons between months  
function onCustomNext() {
  //Update the UI to show the next month passing in "YYYY-MM"
  updateAfterNav(monthVisible.value, 'next') 
  handleTimeframeChange('init')
}
//Same as above but subtract from current month to get previous month
function onCustomPrev() {
  updateAfterNav(monthVisible.value, 'prev') 
  handleTimeframeChange('init')
}

//Go back todays date on month view
function onCustomToday() {
  scrollToToday()
  // update the timeframe and currentDateSelected 
  timeframe.value = new Date(new Date().setHours(0, 0, 0, 0))
  currentDateSelected.value = timeframe.value
  //Get todays date formatted as yyyy-mm-dd
  let formatDate = moment(new Date(), fc_ref).format('yyyy-MM-DD')
  //Find the cell with todays date and update the UI to show it is selected
  let todayCell = document.querySelector(`td[data-date="${formatDate}"]`)
  updateDayIndicator(todayCell)
}

//Scroll the multi calendar view to today's month
function scrollToToday() {
    let calendarScroll = document.querySelector('.fc-customMultiMonth-view')
    if(calendarScroll) {
      const date = new Date()
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      //Find the element with the date of yyyy-mm for todays date and scroll to it
      let startMonthElement = document.querySelector(`[data-date="${year}-${month}"]`)
      calendarScroll.scrollLeft = startMonthElement.offsetLeft
    }
}

//The scroll observer callback function
function onIntersection(entries) {
  entries.forEach(entry => {

    //If a month element has scrolled into view
    if (entry.isIntersecting) {

      //Get the date from the element that has intersected 
      let dateStr = entry.target.getAttribute('data-date')
      
      //Update currently visible month
      monthVisible.value = dateStr

      //Checking if calendar view changed via a swipe/scroll between months
      //Or calendar view has been updated offscreen i.e a date selected via day or week view
      if(updateScrollOffscreen.value == '') {
        updateAfterNav(dateStr, 'swipe')
        //Get events to populate agenda
        getEventsSwipe()
      } else {
        updateAfterNav(updateScrollOffscreen.value, 'offscreen')
        //Reset the offscreen update value once UI has updated
        updateScrollOffscreen.value = ''
      }
    }
  })
}

//Same functionlaity to get events that happens in handleTimeframeChange()
//Needed a way to get a new events without hitting all the other functionality in handleTimeframeChange() that could cause a loop with updateAfterNav()
function getEventsSwipe() {
  let endDate = new Date(timeframe.value.getFullYear(), timeframe.value.getMonth() + 1, 0)
  timeframe.value.setHours(0, 0, 0, 0)
  endDate.setHours(23, 59, 59, 999)
  let info = {start: timeframe.value, end: endDate}
  getEvents(info, (response) => {eventsList.value = response})
}

//Check if there are months that have a 6th row
function identifyTallMonths() {
  let months = document.querySelectorAll('.fc-multimonth-month')

  months.forEach((element) => {
    if(!tallMonths.value) {
      if(element.querySelectorAll('.fc-multimonth-daygrid-table tr').length > 5) {
        tallMonths.value = true
      }
    }
  })
}

// If we have tall months, check the number of rows in the currently visible month and move the agenda up if required
function moveAgendaUp() {
  if(tallMonths.value) {
    let rowTotal = document.querySelectorAll(`[data-date="${monthVisible.value}"] .fc-multimonth-daygrid-table tr`)
  
    if(rowTotal.length === 5) {
      return 'agenda-up'
    } else if(rowTotal.length > 5) {
      return ''
    }
  }
}

//Hide the agenda while scrolling between months. Use debouncer function to show agenda agian when scrolling stops
function hideAgendaScroll() {

  //Remove any set width the calendar may have
  document.querySelector('.fc-customMultiMonth-view').style.width = 'auto'

  const monthAgenda = document.querySelector('.agendaContainer.Month')
  if(monthAgenda) {
    //Hide visible agenda on scroll
    monthAgenda.style.display = 'none'
    
    //Debouncer function to show agenda once scrolling stopped
    let debounceFn = debounce(showAgendaAfterScroll, 500)
    debounceFn()
  }
}

//Show agenda after scrolling to a month
function showAgendaAfterScroll() {
  const monthAgenda = document.querySelector('.agendaContainer.Month')
  if(monthAgenda) {
    //Get the width of the calendar and set it before a potential scrollbar appears otherwise the calendar will resize which will trigger the scroll event again which will hide the agenda and the scrollbar which will resize the calendar which will trigger the scroll event again etc etc.
    const scrollElement = document.querySelector('.fc-customMultiMonth-view');
    const width = scrollElement.getBoundingClientRect().width;
    scrollElement.style.width = width+'px';
    monthAgenda.style.display = 'block'
  }
}

function debounce(func, delay) {
  let timeout = null
  return () => {
      if(timeout) clearTimeout(timeout)

      timeout=setTimeout(() => {
          func()
      }, delay)
  }
}

function hidePreviousDates() {
    //Check for any month elements currently hiding events
    //If any, remove  disable-events class
    let currentlyHidden = document.querySelectorAll('.fc-multimonth-month.disable-events')
    if (currentlyHidden) {
      currentlyHidden.forEach(
        (element) => {
          element.classList.remove('disable-events')
        }
      )
    }

    //Get all the month elements
    let months = document.querySelector('.fc-customMultiMonth-view').childNodes
    let foundCurrent = false
    let prevMonths = []
    //Loop through each month element until we find the month currently being displayed
    months.forEach(
        (month) => {
            //While we haven't found the currently visible month
            if (foundCurrent == false) {
                //If this loops element's month date is the month currently being shown
                if(month.dataset.date == monthVisible.value){
                    //Set found flag
                    foundCurrent = true
                } else {
                    //Add month date to array of previous months
                    prevMonths.push(month.dataset.date)
                }

            }
        }
    )
    //Loop through any previous months and set 'disable-events' class
    if(prevMonths.length > 0) {
        prevMonths.forEach(
            (month) => {
                document.querySelector(`[data-date='${month}']`).classList.add('disable-events')
            }
        )
    }
}

function keyUpHandler(event) {
  if(event.key === 'Enter') {
    if(event.target.classList.contains("fc-daygrid-dot-event")) {
      document.querySelector('.agendaEvent').focus()
    }
  }
}

function weekHandler() {
  weekAgendaBlank.value = false
}

/*********************************
  Class Popup
*********************************/ 

</script>

<style lang='scss'>
/*@import '@/scss/fullcalendar-customizations.scss';*/

/**********
Time Frame View Navigation
***********/
.timeframeControlsContainer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height:50px;
  padding-right: 8px;
  width: 100%;
}

.timeframeControlsContainer.Week {
    justify-content: space-between;
    padding-left: 8px;
}

.timeframeControlsContainer #weekSelected p {
    margin-bottom: 0px;
}

.timeframeControlsContainer #buttonWrapper {
    display: flex;
}

.timeframeControlsContainer button {
  border: none;
  background-color: #fff;
}

.timeframeControlsContainer .fc-customToday-button {
  /* to match full calendar css */
  padding: 0.4em 0.65em;
}

.viewSelector {
  display:flex;
  background-color: var(--uog-blue-10);
  height: 44px;
}

.viewSelectorColumn {
  width:calc(100% / 3);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: var(--uog-dark-blue);
}

.viewSelectorColumn button {
    height: 44px;
    width: 100%;
    background-color: transparent;
    border: none;
    color: var(--uog-dark-blue);
}

.selectedView {
  border-bottom: 2px solid var(--uog-dark-blue);
}

/* Multimonth view nav buttons */
.fc .fc-toolbar.fc-header-toolbar {
  margin-bottom: 1rem;
  height: 50px;
}

/*overwriting default*/
.fc-direction-ltr .fc-header-toolbar .fc-button-group > .fc-button:not(:first-child) {
  margin-left:0px;
}

.fc-header-toolbar .fc-button-primary {
    background: transparent;
    color: var(--uog-dark-blue);
    border: none;
    height: 44px;
    background-repeat: no-repeat;
    background-position: center;
    font-weight:bold;
}

.fc-header-toolbar .fc-button-primary:hover {
  background-color:transparent;
  color: var(--uog-dark-blue); 
  cursor: pointer;
}

.fc-header-toolbar .fc-button-primary.fc-customToday-button {
  margin-right:2px;
}

.fc-header-toolbar .fc-button-primary.fc-customPrev-button,
.fc-header-toolbar .fc-button-primary.fc-customNext-button {
    padding:0px 15px;
    background-size: 20px;
}

.fc-header-toolbar .fc-button-primary.fc-customNext-button {
    background-image: url('@/assets/far-chevron-right.svg');
}

.fc-header-toolbar .fc-button-primary.fc-customNext-button:active {
  background-image: url('@/assets/far-chevron-right-white.svg');
}

.fc-header-toolbar .fc-button-primary.fc-customNext-button.disable-btn {
  background-image: url('@/assets/far-chevron-right-disabled.svg');
}

.fc-header-toolbar .fc-button-primary.fc-customPrev-button {
    background-image: url('@/assets/far-chevron-left.svg');
}

.fc-header-toolbar .fc-button-primary.fc-customPrev-button:active {
    background-image: url('@/assets/far-chevron-left-white.svg');
}

.fc-header-toolbar .fc-button-primary.fc-customPrev-button.disable-btn {
    background-image: url('@/assets/far-chevron-left-disabled.svg');
}

.fc-header-toolbar .fc-button-primary:not(:disabled):active {
    background-color: var(--uog-dark-blue);
    border-radius:4px;
    color:#fff;
}

.fc-header-toolbar .fc-button-primary:not(:disabled):focus {
  border: 2px solid #000;
  border-radius: 0.25em;
}

.fc-header-toolbar .fc-button-primary:disabled {
  background-color: transparent;
  cursor: auto;
}

/**********
Month View
 ***********/
#calendar {
  max-width: 540px;
  margin: 0 auto;
}

/* Multimonth container */
.fc .fc-multimonth { 
  border:none;
  flex-wrap: nowrap;
  overflow: scroll hidden;
  scroll-snap-type: x mandatory; 
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
    scrollbar-width: none;  /* Firefox */ 
}

.fc .fc-multimonth::-webkit-scrollbar { 
    display: none;  /* Safari and Chrome */
}

/* Bug with width not being set when viewing month view so evething was smushed */
.fc-multimonth-header-table,
.fc .fc-daygrid-body,
.fc-multimonth-daygrid-table {
  min-width:100%;
}

.fc-view-harness {
  overflow:hidden;
}

/* Month grid wrappers */
.fc-multimonth-month {
    min-width: 100%;
    scroll-snap-align: center;
    scroll-snap-stop: always;
    margin: 0 20px;
}

/*Hide the original day of week table header*/
.fc-multimonth-header .fc-multimonth-header-table {
    display: none;
}

/* Day of the week table headers */
.fc-customMultiMonth-view th,
#daysHeader th {
    border: none;
}
/* Day of the week table header text */
.fc-multimonth-header-table th a {
    font-size:14px;
    color: #323232;
    text-decoration:none;
    font-weight:normal;
}
table.fc-multimonth-header-table th.weekend-day a {
    color: #757575;
}
.fc-col-header-cell .fc-scrollgrid-sync-inner {
    text-align: left;
}

/*Hide the original month title*/
.fc .fc-multimonth-title {
    display: none;
}

/* Daygrid table */
.fc-multimonth-daygrid-table {
  /*overwrite inline-style*/
  height: auto!important;
}

/* Container around daygrid */
.fc-multimonth-daygrid {
    border-top: solid 1px #CCCCCC;
}

/* Day grid cell */
.fc-theme-standard td {
    border: none;
    border-top: solid 1px #CCCCCC;
}
/* Disabled day (past/furture months) */
.fc .fc-day-disabled {
    background:none;
}

/* Container around day cell contents */
.fc .fc-daygrid-day-frame {
    min-height: 48px;
}

/* Wrapper around day cell number */
.fc .fc-daygrid-day-top {
    flex-direction: row;
}

/* Day cell numbers */
.fc .fc-daygrid-day-number {
    font-size: 19px;
    color: #1D1D1D;
    line-height: 24px;
    text-decoration: none;
}
.fc-daygrid-body .fc-day-past .fc-daygrid-day-number {
    color: #888888;
}

.fc .fc-daygrid-day-frame,
.fc-daygrid-day-number {
  cursor: pointer;
}

/* Today's cell */
.fc .fc-daygrid-day.fc-day.fc-day-today {
    background: transparent;
}

/* Circle around today's number */
.fc-daygrid-body .fc-day-today .fc-daygrid-day-number {
    background: var(--uog-dark-blue);
    border-radius: 50%;
    height: 33px;
    width: 34px;
    text-align: center;
    color: #fff;
    text-indent: -1px;
}

/*Remove gray colour from event cell focus*/
.fc-event-selected::after,
.fc-event:focus::after {
  background: none;
}
.fc-event-selected,
.fc-event:focus {
  box-shadow:none;
}

/* Event dot */
.fc-daygrid-event-dot {
    /*
    Original line, can replace variables with our own 
    border: calc(var(--fc-daygrid-event-dot-width, 8px) / 2) solid var(--fc-event-border-color, #3788d8);
    */
    border: 3.5px solid #6D87A0;
    margin-bottom: 3px;
}

.fc-daygrid-dot-event:hover {
  background: none;
}

/* Make cell content elements same height as cell */
.fc .fc-daygrid-event-harness,
.fc .fc-daygrid-body-balanced .fc-daygrid-day-events,
a.fc-daygrid-event.fc-daygrid-dot-event.fc-event {
    position: absolute;
    height: 45px;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    align-items: flex-end;
    /*overwrite inline-style to show dot when 'more link' present */
    visibility: visible!important;
}

/* Hide all of the event dot containers */
.fc-daygrid-event-harness {
    display: none;
}
/* Only ever show the first event dot */
.fc-daygrid-event-harness:first-child {
    display: block;
}

/* Hide more link if present */
.fc .fc-daygrid-more-link {
  display:none;
}

/* Hide events from months previous to the one being shown (for accessibility when tabbing through via keyboard) */
.fc-multimonth-month.disable-events .fc-daygrid-dot-event {
    display: none;
}




/**********
Month Picker - Month View
 ***********/
#selectWrap {
    position:relative;
}

/* Pseudo element for the chevron */
#selectWrap::after {
    display: block;
    content: "";
    background-image:url('@/assets/far-chevron-down.svg');
    background-repeat: no-repeat;
    background-size: cover;
    position: absolute;
    height: 19px;
    width: 19px;
    top: 12px;
    right: 7px;
    -webkit-transform: translateY(-50%);
    transform: translateY(-50%);
    z-index: 1;
}

#uogMonthPick {
    height: 55px;
    text-align: center;
    font-size: 19px;
    display: flex;
    align-items: center;
    justify-content: center;
}

#monthPick {
  color:#323232;
  line-height:24px;
  border: none;
  text-align: right;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding-right: 31px;
  background: transparent;
  position: relative;
  z-index: 5;
}

/**********
Month select modal
 ***********/
/* Pretty much the same styling as support and wellbeing modal
should be combined at somepoint */
/* Targeting month options model */
#monthModal .modal-container{
    width: 90%;
    max-width: 800px;
    padding: 0px;
    overflow: auto;
}

#monthModal .modal-header {
    background-color: #fff;
    color: var(--uog-dark-blue);
    padding-left: 20px;
}

#monthModal .modal-container,
#monthModal .modal-header {
    border-radius:8px;
}

#monthModal .modal-body {
    /*padding:0px 20px;*/
    padding:0px;
    overflow-y: auto;
    max-height: calc(100vh - 200px);
    margin-bottom: 40px;
}

#monthModal .title {
    color: var(--uog-dark-blue);
    font-weight: bold;
}

#monthModal .month-option {
    cursor:pointer;
}

#monthModal .month-option:hover {
    background-color: var(--uog-dark-blue);
    color:#fff;
}

#monthModal ul {
    list-style-type: none;
    padding-left: 0px;
}

#monthModal .bookingItemIcon {
  color: var(--uog-dark-blue);
}

.month-option {
    border-bottom: solid 1px rgba(0, 0, 0, 0.10);
    padding: 8px 24px;
}

.month-option:last-child {
    border-bottom: 0px;
}

.month-option.selected {
    border-left: 10px solid var(--uog-dark-blue);
    font-weight:bold;
    padding-left: 14px;
}

/**********
Month view agenda
***********/
/* Hide day title bar on month view */
.agendaContainer.Month .weekViewDateTitle {
    display: none;
}

.agenda-up {
  margin-top:-30px;
}

</style>
